import { formatPersonName } from './formatPersonName';

const ACTIVE_CONTACT_STORAGE_KEY = 'config-go-active-contact';
const LEGACY_SESSION_KEY = 'config-go-active-contact';

/** Partner channel for end-clients not in GHL/CRM (Duurzaamadviesloket). */
export const DUURZAAM_PARTNER_TYPE = 'duurzaamadviesloket';
export const DUURZAAM_PARTNER_LABEL = 'Duurzaamadviesloket';
export const DUURZAAM_DEFAULT_EMAIL = 'service@duurzaamadviesloket.nl';
export const DUURZAAM_DEFAULT_PHONE = '+31628615645';

export function getDuurzaamDefaultFormValues() {
  return {
    endClientName: '',
    email: DUURZAAM_DEFAULT_EMAIL,
    phone: DUURZAAM_DEFAULT_PHONE,
    address: '',
    postalCode: '',
    city: '',
  };
}

export function isDuurzaamContact(contact) {
  if (!contact || typeof contact !== 'object') return false;
  return (
    contact.partnerType === DUURZAAM_PARTNER_TYPE ||
    Boolean(contact.endClientName) ||
    String(contact.name || '').toLowerCase().startsWith(`${DUURZAAM_PARTNER_LABEL.toLowerCase()} /`)
  );
}

/**
 * Display name for quote cover "Voor:" and lists.
 * Duurzaam offers: "Duurzaamadviesloket / End Client".
 */
export function formatQuoteClientDisplayName(clientDetailsOrName) {
  if (clientDetailsOrName && typeof clientDetailsOrName === 'object') {
    if (isDuurzaamContact(clientDetailsOrName)) {
      const end = formatPersonName(
        clientDetailsOrName.endClientName ||
          String(clientDetailsOrName.name || '').replace(
            new RegExp(`^${DUURZAAM_PARTNER_LABEL}\\s*/\\s*`, 'i'),
            '',
          ),
      );
      return end ? `${DUURZAAM_PARTNER_LABEL} / ${end}` : DUURZAAM_PARTNER_LABEL;
    }
    return formatPersonName(clientDetailsOrName.name) || '';
  }

  const raw = String(clientDetailsOrName || '').trim();
  if (!raw) return '';
  const match = raw.match(new RegExp(`^${DUURZAAM_PARTNER_LABEL}\\s*/\\s*(.*)$`, 'i'));
  if (match) {
    const end = formatPersonName(match[1] || '');
    return end ? `${DUURZAAM_PARTNER_LABEL} / ${end}` : DUURZAAM_PARTNER_LABEL;
  }
  return formatPersonName(raw);
}

export function buildDuurzaamadviesloketContact({
  endClientName = '',
  email = '',
  phone = '',
  address = '',
  city = '',
  postalCode = '',
} = {}) {
  const end = formatPersonName(String(endClientName || '').trim());
  if (!end) return null;

  return {
    id: null,
    partnerType: DUURZAAM_PARTNER_TYPE,
    partnerLabel: DUURZAAM_PARTNER_LABEL,
    endClientName: end,
    name: `${DUURZAAM_PARTNER_LABEL} / ${end}`,
    address: String(address || '').trim(),
    city: String(city || '').trim(),
    postalCode: String(postalCode || '').trim(),
    phone: String(phone || '').trim(),
    email: String(email || '').trim(),
    country: '',
  };
}

export function buildSelectedContactPayload(contact) {
  if (!contact) return null;

  if (isDuurzaamContact(contact)) {
    return buildDuurzaamadviesloketContact({
      endClientName:
        contact.endClientName ||
        String(contact.name || '').replace(
          new RegExp(`^${DUURZAAM_PARTNER_LABEL}\\s*/\\s*`, 'i'),
          '',
        ),
      email: contact.email,
      phone: contact.phone,
      address: contact.address,
      city: contact.city,
      postalCode: contact.postal_code || contact.postalCode,
    });
  }

  const name = formatPersonName(`${contact.first_name || ''} ${contact.last_name || ''}`.trim());
  return {
    id: contact.id,
    name,
    address: contact.address || '',
    city: contact.city || '',
    postalCode: contact.postal_code || contact.postalCode || '',
    phone: contact.phone || '',
    email: contact.email || '',
    country: contact.country || '',
  };
}

/** Map quote clientDetails → selectedContact shape used on configurator-select. */
export function buildSelectedContactFromClientDetails(clientDetails) {
  if (!clientDetails) return null;

  if (isDuurzaamContact(clientDetails)) {
    const end =
      clientDetails.endClientName ||
      String(clientDetails.name || '').replace(
        new RegExp(`^${DUURZAAM_PARTNER_LABEL}\\s*/\\s*`, 'i'),
        '',
      );
    return buildDuurzaamadviesloketContact({
      endClientName: end,
      email: clientDetails.email,
      phone: clientDetails.phone,
      address: clientDetails.address,
      city: clientDetails.city,
      postalCode: clientDetails.postalCode,
    });
  }

  const name = formatPersonName(clientDetails.name || '');
  const email = (clientDetails.email || '').trim();
  if (!name && !email) return null;

  return {
    id: clientDetails.contactId ?? clientDetails.id ?? null,
    name,
    address: clientDetails.address || '',
    city: clientDetails.city || '',
    postalCode: clientDetails.postalCode || '',
    phone: clientDetails.phone || '',
    email,
    country: clientDetails.country || '',
  };
}

function readStoredContactRecord() {
  try {
    const raw = localStorage.getItem(ACTIVE_CONTACT_STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch {
    // Ignore parse errors
  }

  try {
    const legacy = sessionStorage.getItem(LEGACY_SESSION_KEY);
    if (!legacy) return null;
    const parsed = JSON.parse(legacy);
    if (parsed?.id || parsed?.name || parsed?.partnerType) {
      return { userId: null, contact: parsed };
    }
  } catch {
    // Ignore parse errors
  }

  return null;
}

export function persistActiveContactForConfigurator(contact, userId = null) {
  if (!contact) return null;

  let payload = null;
  if (isDuurzaamContact(contact)) {
    payload = buildDuurzaamadviesloketContact({
      endClientName:
        contact.endClientName ||
        String(contact.name || '').replace(
          new RegExp(`^${DUURZAAM_PARTNER_LABEL}\\s*/\\s*`, 'i'),
          '',
        ),
      email: contact.email,
      phone: contact.phone,
      address: contact.address,
      city: contact.city,
      postalCode: contact.postal_code || contact.postalCode,
    });
  } else if (contact.first_name != null || contact.last_name != null) {
    payload = buildSelectedContactPayload(contact);
  } else {
    payload = buildSelectedContactFromClientDetails(contact) || buildSelectedContactPayload(contact);
  }

  if (!payload) return null;

  try {
    localStorage.setItem(
      ACTIVE_CONTACT_STORAGE_KEY,
      JSON.stringify({ userId: userId ?? null, contact: payload })
    );
    sessionStorage.removeItem(LEGACY_SESSION_KEY);
  } catch {
    // Ignore storage errors (private browsing, quota, etc.)
  }

  return payload;
}

export function readActiveContactForConfigurator(userId = null) {
  const record = readStoredContactRecord();
  if (!record?.contact) return null;

  if (userId != null && record.userId != null && record.userId !== userId) {
    return null;
  }

  return record.contact;
}

export function clearActiveContactForConfigurator() {
  try {
    localStorage.removeItem(ACTIVE_CONTACT_STORAGE_KEY);
    sessionStorage.removeItem(LEGACY_SESSION_KEY);
  } catch {
    // Ignore storage errors
  }
}

export function resolveConfiguratorContactState(locationState, userId = null) {
  const fromState = locationState?.selectedContact;
  if (fromState?.name || fromState?.email || isDuurzaamContact(fromState)) {
    return fromState;
  }

  return readActiveContactForConfigurator(userId);
}

/**
 * Merge CRM / session contact into quote clientDetails.
 * preferActiveContact true: incoming contact wins (explicit pick from contact profile).
 * preferActiveContact false: existing quote clientDetails win; contact fills empty fields only.
 */
export function applyActiveContactToClientDetails(
  clientDetails = {},
  selectedContact,
  { preferActiveContact = false } = {},
) {
  if (!selectedContact) return { ...clientDetails };

  const pick = (current, incoming) => {
    const value = (current || '').trim();
    const next = (incoming || '').trim();
    if (preferActiveContact) return next || value;
    return value || next;
  };

  if (isDuurzaamContact(selectedContact)) {
    const duurzaam = buildDuurzaamadviesloketContact({
      endClientName:
        selectedContact.endClientName ||
        String(selectedContact.name || '').replace(
          new RegExp(`^${DUURZAAM_PARTNER_LABEL}\\s*/\\s*`, 'i'),
          '',
        ),
      email: selectedContact.email,
      phone: selectedContact.phone,
      address: selectedContact.address,
      city: selectedContact.city,
      postalCode: selectedContact.postalCode,
    });
    if (!duurzaam) return { ...clientDetails };

    if (!preferActiveContact) {
      const hasExisting =
        (clientDetails.name || '').trim() || (clientDetails.email || '').trim();
      if (hasExisting && !isDuurzaamContact(clientDetails)) {
        return { ...clientDetails };
      }
    }

    return {
      ...clientDetails,
      name: duurzaam.name,
      endClientName: duurzaam.endClientName,
      partnerType: DUURZAAM_PARTNER_TYPE,
      partnerLabel: DUURZAAM_PARTNER_LABEL,
      address: preferActiveContact
        ? duurzaam.address || clientDetails.address || ''
        : pick(clientDetails.address, duurzaam.address),
      city: preferActiveContact
        ? duurzaam.city || clientDetails.city || ''
        : pick(clientDetails.city, duurzaam.city),
      postalCode: preferActiveContact
        ? duurzaam.postalCode || clientDetails.postalCode || ''
        : pick(clientDetails.postalCode, duurzaam.postalCode),
      phone: preferActiveContact
        ? duurzaam.phone || clientDetails.phone || ''
        : pick(clientDetails.phone, duurzaam.phone),
      email: preferActiveContact
        ? duurzaam.email || clientDetails.email || ''
        : pick(clientDetails.email, duurzaam.email),
      contactId: null,
    };
  }

  const contactName = formatPersonName(selectedContact.name) || '';
  const next = {
    ...clientDetails,
    name: pick(clientDetails.name, contactName),
    address: pick(clientDetails.address, selectedContact.address),
    city: pick(clientDetails.city, selectedContact.city),
    postalCode: pick(clientDetails.postalCode, selectedContact.postalCode),
    phone: pick(clientDetails.phone, selectedContact.phone),
    email: pick(clientDetails.email, selectedContact.email),
    contactId: selectedContact.id ?? clientDetails.contactId,
  };

  if (preferActiveContact) {
    delete next.partnerType;
    delete next.partnerLabel;
    delete next.endClientName;
  }

  return next;
}

export function buildConfiguratorNavigationState(locationState, overrides = {}, userId = null) {
  const selectedContact =
    overrides.selectedContact || resolveConfiguratorContactState(locationState, userId);

  const safeState = {
    returnTo: locationState?.returnTo,
    fromContactProfile: locationState?.fromContactProfile,
    from: locationState?.from,
    preserveQuoteSession: locationState?.preserveQuoteSession,
  };

  const { selectedContact: _ignored, ...restOverrides } = overrides;

  return {
    ...safeState,
    ...restOverrides,
    ...(selectedContact ? { selectedContact } : {}),
  };
}

/**
 * When editing a saved backend quote, apply an explicitly picked CRM contact to that quote session only.
 */
export function applyExplicitContactToBackendQuoteSession(
  activeBackendQuote,
  selectedContact,
  { isEditingExisting = false } = {},
) {
  if (!isEditingExisting || !activeBackendQuote?.id || !selectedContact) {
    return null;
  }

  return {
    clientDetails: applyActiveContactToClientDetails(
      activeBackendQuote.clientDetails || {},
      selectedContact,
      { preferActiveContact: true },
    ),
    clientContactOverride: true,
  };
}
